import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Building2,
  User,
  Info,
  Clock,
  CheckCircle,
  XCircle,
  Cloud,
  Loader2,
  ExternalLink,
  Pencil,
  Save,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatDate } from "@/lib/format";
import { toastError } from "@/lib/toast-error";
import {
  AddressInput,
  AddressValue,
  addressFromApi,
  addressToPayload,
  formatAddressDisplay,
} from "@/components/AddressInput";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface LocalDealDetail {
  id: string;
  sfOpportunityId?: string;
  sfAccountId?: string;
  accountName: string;
  opportunityName: string;
  ownerName?: string;
  ownerEmail?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  billingAddress?: { addressLine1?: string; addressLine2?: string; city?: string; state?: string; postalCode?: string; country?: string };
  closeDate?: string;
  mrr?: number;
  tcv?: number;
  products?: string[];
  notes?: string;
  origin: string;
  approvalStatus?: string;
  approvedAt?: string;
  approvalNotes?: string;
  createdAt: string;
  suppliers: Array<{
    id: string;
    sfAccountId: string;
    accountName: string;
    routingBasis?: string;
    channelManagers: Array<{ name: string; email: string; phone?: string; title?: string; supportedStates?: string }>;
    overrideUsed?: { name: string; email: string };
    dealOverride?: { channelManagerId?: string; name: string; email: string };
  }>;
  events?: Array<{
    id: string;
    type: string;
    message?: string;
    actorEmail?: string;
    createdAt: string;
  }>;
}

function StatusBadge({ status }: { status?: string }) {
  if (status === "approved") return <Badge className="bg-green-100 text-green-800 border-green-300"><CheckCircle className="mr-1 h-3 w-3" />Approved</Badge>;
  if (status === "denied") return <Badge className="bg-red-100 text-red-800 border-red-300"><XCircle className="mr-1 h-3 w-3" />Denied</Badge>;
  return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300"><Clock className="mr-1 h-3 w-3" />Pending Approval</Badge>;
}

interface EditFormState {
  accountName: string;
  opportunityName: string;
  ownerName: string;
  ownerEmail: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  closeDate: string;
  mrr: string;
  tcv: string;
  notes: string;
}

function dealToForm(d: LocalDealDetail): EditFormState {
  return {
    accountName: d.accountName ?? "",
    opportunityName: d.opportunityName ?? "",
    ownerName: d.ownerName ?? "",
    ownerEmail: d.ownerEmail ?? "",
    primaryContactName: d.primaryContactName ?? "",
    primaryContactEmail: d.primaryContactEmail ?? "",
    primaryContactPhone: d.primaryContactPhone ?? "",
    closeDate: d.closeDate ?? "",
    mrr: d.mrr != null ? String(d.mrr) : "",
    tcv: d.tcv != null ? String(d.tcv) : "",
    notes: d.notes ?? "",
  };
}

export default function LocalDealDetail({ params }: { params: { id: string } }) {
  const queryClient = useQueryClient();
  const dealQueryKey = [`${BASE}/api/local-deals/${params.id}`];
  const { data: deal, isLoading, error } = useQuery<LocalDealDetail>({
    queryKey: dealQueryKey,
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/local-deals/${params.id}`, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json())?.error ?? "Not found");
      return r.json();
    },
  });

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<EditFormState>(() => deal ? dealToForm(deal) : {
    accountName: "", opportunityName: "", ownerName: "", ownerEmail: "",
    primaryContactName: "", primaryContactEmail: "", primaryContactPhone: "",
    closeDate: "", mrr: "", tcv: "", notes: "",
  });
  const [billingAddress, setBillingAddress] = useState<AddressValue>(addressFromApi(deal?.billingAddress));

  function startEditing() {
    if (!deal) return;
    setForm(dealToForm(deal));
    setBillingAddress(addressFromApi(deal.billingAddress));
    setIsEditing(true);
  }

  function cancelEditing() {
    if (deal) {
      setForm(dealToForm(deal));
      setBillingAddress(addressFromApi(deal.billingAddress));
    }
    setIsEditing(false);
  }

  // If the deal data refreshes while not editing, sync the form so it reflects
  // the latest server state when the user does click Edit.
  useEffect(() => {
    if (deal && !isEditing) {
      setForm(dealToForm(deal));
      setBillingAddress(addressFromApi(deal.billingAddress));
    }
  }, [deal, isEditing]);

  const editMutation = useMutation<LocalDealDetail, Error>({
    mutationFn: async () => {
      const accountName = form.accountName.trim();
      const opportunityName = form.opportunityName.trim();
      if (!accountName || !opportunityName) {
        throw new Error("Account name and opportunity name are required");
      }
      const r = await fetch(`${BASE}/api/local-deals/${params.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountName,
          opportunityName,
          ownerName: form.ownerName.trim(),
          ownerEmail: form.ownerEmail.trim(),
          primaryContactName: form.primaryContactName.trim(),
          primaryContactEmail: form.primaryContactEmail.trim(),
          primaryContactPhone: form.primaryContactPhone.trim(),
          closeDate: form.closeDate.trim(),
          mrr: form.mrr.trim() ? Number(form.mrr) : null,
          tcv: form.tcv.trim() ? Number(form.tcv) : null,
          notes: form.notes.trim(),
          billingAddress: addressToPayload(billingAddress) ?? {},
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.error ?? `Update failed (HTTP ${r.status})`);
      }
      return r.json();
    },
    onSuccess: (updated) => {
      toast.success("Deal updated");
      queryClient.setQueryData(dealQueryKey, updated);
      queryClient.invalidateQueries({ queryKey: [`${BASE}/api/local-deals`] });
      setIsEditing(false);
    },
    onError: (err) => toastError(err),
  });

  const pushMutation = useMutation<
    { sfOpportunityId?: string; salesforce?: { opportunityId: string; url?: string; warnings: string[] } },
    Error
  >({
    mutationFn: async () => {
      const r = await fetch(`${BASE}/api/local-deals/${params.id}/push-to-salesforce`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.error ?? `Push failed (HTTP ${r.status})`);
      }
      return r.json();
    },
    onSuccess: (data) => {
      const oppId = data.salesforce?.opportunityId ?? data.sfOpportunityId;
      toast.success(`Pushed to Salesforce${oppId ? ` (${oppId})` : ""}`);
      if (data.salesforce?.warnings?.length) {
        toast.warning(data.salesforce.warnings.join("; "));
      }
      queryClient.invalidateQueries({ queryKey: dealQueryKey });
    },
    onError: (err) => {
      toastError(err);
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-4xl mx-auto">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="text-center py-16 text-destructive">
        <p className="font-medium">Deal not found or access denied.</p>
        <Link href="/deals"><Button variant="link" className="mt-2">Back to Deals</Button></Link>
      </div>
    );
  }

  const canEdit = !deal.sfOpportunityId;

  function setField(k: keyof EditFormState, v: string) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto animate-in fade-in duration-500">
      <div className="flex items-center gap-4">
        <Link href="/deals">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold truncate">{deal.opportunityName}</h1>
            <StatusBadge status={deal.approvalStatus} />
          </div>
          <p className="text-muted-foreground">{deal.accountName}</p>
        </div>
        {!isEditing && canEdit && (
          <Button variant="outline" size="sm" onClick={startEditing}>
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
        )}
      </div>

      {isEditing ? (
        <Card>
          <CardHeader>
            <CardTitle>Edit Deal</CardTitle>
            <CardDescription>
              Update deal details. Changes are recorded in the audit trail.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-accountName">Account / Customer Name <span className="text-destructive">*</span></Label>
                <Input id="edit-accountName" value={form.accountName} onChange={(e) => setField("accountName", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-opportunityName">Opportunity Name <span className="text-destructive">*</span></Label>
                <Input id="edit-opportunityName" value={form.opportunityName} onChange={(e) => setField("opportunityName", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-ownerName">Deal Owner Name</Label>
                <Input id="edit-ownerName" value={form.ownerName} onChange={(e) => setField("ownerName", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-ownerEmail">Deal Owner Email</Label>
                <Input id="edit-ownerEmail" type="email" value={form.ownerEmail} onChange={(e) => setField("ownerEmail", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-primaryContactName">Primary Contact Name</Label>
                <Input id="edit-primaryContactName" value={form.primaryContactName} onChange={(e) => setField("primaryContactName", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-primaryContactEmail">Primary Contact Email</Label>
                <Input id="edit-primaryContactEmail" type="email" value={form.primaryContactEmail} onChange={(e) => setField("primaryContactEmail", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-primaryContactPhone">Primary Contact Phone</Label>
                <Input id="edit-primaryContactPhone" value={form.primaryContactPhone} onChange={(e) => setField("primaryContactPhone", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-closeDate">Target Close Date</Label>
                <Input id="edit-closeDate" type="date" value={form.closeDate} onChange={(e) => setField("closeDate", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-mrr">Estimated MRR ($)</Label>
                <Input id="edit-mrr" type="number" min="0" step="0.01" value={form.mrr} onChange={(e) => setField("mrr", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-tcv">Estimated TCV ($)</Label>
                <Input id="edit-tcv" type="number" min="0" step="0.01" value={form.tcv} onChange={(e) => setField("tcv", e.target.value)} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Billing / Customer Address</Label>
                <AddressInput value={billingAddress} onChange={setBillingAddress} idPrefix="edit-ba" labelPrefix="Billing" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="edit-notes">Internal Notes</Label>
                <Textarea id="edit-notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="resize-none" />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-2">
            <Button variant="outline" onClick={cancelEditing} disabled={editMutation.isPending}>
              <X className="mr-2 h-4 w-4" /> Cancel
            </Button>
            <Button
              onClick={() => editMutation.mutate()}
              disabled={editMutation.isPending || !form.accountName.trim() || !form.opportunityName.trim()}
            >
              {editMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save Changes
            </Button>
          </CardFooter>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Deal Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <dl className="space-y-2">
                <div className="flex justify-between"><dt className="text-muted-foreground">Account</dt><dd className="font-medium">{deal.accountName}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Opportunity</dt><dd className="font-medium">{deal.opportunityName}</dd></div>
                {deal.ownerName && <div className="flex justify-between"><dt className="text-muted-foreground">Owner</dt><dd>{deal.ownerName}</dd></div>}
                {deal.ownerEmail && <div className="flex justify-between"><dt className="text-muted-foreground">Owner Email</dt><dd>{deal.ownerEmail}</dd></div>}
                {deal.billingAddress && formatAddressDisplay(deal.billingAddress) && (
                  <div className="flex justify-between gap-4"><dt className="text-muted-foreground shrink-0">Billing Address</dt><dd className="text-right">{formatAddressDisplay(deal.billingAddress)}</dd></div>
                )}
                {deal.closeDate && <div className="flex justify-between"><dt className="text-muted-foreground">Close Date</dt><dd>{formatDate(deal.closeDate)}</dd></div>}
                {deal.mrr && <div className="flex justify-between"><dt className="text-muted-foreground">MRR</dt><dd>{formatCurrency(deal.mrr)}</dd></div>}
                {deal.tcv && <div className="flex justify-between"><dt className="text-muted-foreground">TCV</dt><dd>{formatCurrency(deal.tcv)}</dd></div>}
              </dl>
              {deal.products && deal.products.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-2 border-t">
                  {deal.products.map((p, i) => <Badge key={i} variant="secondary" className="text-xs">{p}</Badge>)}
                </div>
              )}
              {deal.notes && <p className="text-muted-foreground bg-muted/40 rounded p-2">{deal.notes}</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Approval Status</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2"><StatusBadge status={deal.approvalStatus} /></div>
              {deal.approvedAt && <p className="text-muted-foreground">Approved: {new Date(deal.approvedAt).toLocaleString()}</p>}
              {deal.approvalNotes && <p className="bg-muted/40 rounded p-2">{deal.approvalNotes}</p>}
              {deal.approvalStatus === "pending" && (
                <p className="text-muted-foreground text-xs">An admin will review this deal.</p>
              )}
              {deal.approvalStatus === "approved" && (
                <div className="pt-2 border-t space-y-2">
                  {deal.sfOpportunityId ? (
                    <div className="space-y-1">
                      <p className="font-medium flex items-center gap-1.5 text-green-700">
                        <Cloud className="h-4 w-4" /> In Salesforce
                      </p>
                      <p className="text-xs text-muted-foreground break-all">
                        Opportunity ID: <span className="font-mono">{deal.sfOpportunityId}</span>
                      </p>
                    </div>
                  ) : (
                    <Button
                      onClick={() => pushMutation.mutate()}
                      disabled={pushMutation.isPending}
                      size="sm"
                      className="w-full"
                    >
                      {pushMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <ExternalLink className="mr-2 h-4 w-4" />
                      )}
                      Push to Salesforce
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Suppliers & Channel Managers</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {deal.suppliers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No suppliers attached.</p>
          ) : deal.suppliers.map((s) => (
            <div key={s.id} className="border rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  {s.accountName}
                </h4>
                {s.routingBasis && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Info className="h-3 w-3" />{s.routingBasis}
                  </span>
                )}
              </div>
              {s.dealOverride ? (
                <div className="bg-primary/5 border border-primary/20 rounded p-2 text-xs">
                  <span className="font-medium text-primary">Channel Manager (this deal only): </span>
                  {s.dealOverride.name} &lt;{s.dealOverride.email}&gt;
                </div>
              ) : s.overrideUsed ? (
                <div className="bg-primary/5 border border-primary/20 rounded p-2 text-xs">
                  <span className="font-medium text-primary">Global override: </span>
                  {s.overrideUsed.name} &lt;{s.overrideUsed.email}&gt;
                </div>
              ) : null}
              {s.dealOverride || s.overrideUsed ? null : s.channelManagers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No channel managers</p>
              ) : s.channelManagers.map((cm, i) => (
                <div key={i} className="flex items-start gap-2 p-2 bg-muted/30 rounded text-sm">
                  <User className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="font-medium">{cm.name}</p>
                    <p className="text-muted-foreground">{cm.email}{cm.title ? ` — ${cm.title}` : ""}</p>
                    {cm.supportedStates && <p className="text-xs text-muted-foreground">States: {cm.supportedStates}</p>}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      {deal.events && deal.events.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Audit Trail</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {deal.events.map((e) => (
                <li key={e.id} className="flex items-start gap-3 text-sm">
                  <Clock className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <span className="font-medium capitalize">{e.type.replace(/_/g, " ")}</span>
                    {e.message && <span className="text-muted-foreground"> — {e.message}</span>}
                    <p className="text-xs text-muted-foreground">{e.actorEmail} · {new Date(e.createdAt).toLocaleString()}</p>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
